import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { I18nService } from '../../../core';
import { TenantService } from '../../../core/tenant/tenant.service';
import { OperationsApiService } from '../../../core/api';
import type { WorkflowUiModel } from '../../../core/types';
import { mapWorkflowContractToUiModel } from '../adapters/workflow.adapter';
import { isCompanyChangedAbort, runWithTenantRetries } from '../../../core/utils/tenant-retries';

@Injectable({ providedIn: 'root' })
export class WorkflowsStore {
  private readonly i18n = inject(I18nService);
  private readonly tenant = inject(TenantService);
  private readonly api = inject(OperationsApiService);
  private readonly loadedCompanyIdSignal = signal<string | null>(null);

  private readonly workflowsSignal = signal<WorkflowUiModel[]>([]);
  private readonly loadingSignal = signal<boolean>(false);
  private readonly errorSignal = signal<string | null>(null);

  readonly workflows = this.workflowsSignal.asReadonly();
  readonly loading = this.loadingSignal.asReadonly();
  readonly error = this.errorSignal.asReadonly();
  readonly hasData = computed(() => this.workflows().length > 0);

  constructor() {
    effect(() => {
      void this.tenant.activeCompany();
      const companyId = this.tenant.companyId();
      if (!companyId) {
        this.loadedCompanyIdSignal.set(null);
        this.workflowsSignal.set([]);
        this.loadingSignal.set(false);
        this.errorSignal.set(null);
        return;
      }
      if (this.loadedCompanyIdSignal() === companyId) return;
      void this.reloadAsync();
    });
  }

  load(): void {
    void this.reloadAsync();
  }

  /** Aguarda o GET de workflows (útil após criar workflow — evita abrir página com etapas em cache velho). */
  async reloadAsync(): Promise<void> {
    const requestCompanyId = this.tenant.companyId();
    if (!requestCompanyId) return;

    this.loadingSignal.set(true);
    this.errorSignal.set(null);
    try {
      const workflows = await runWithTenantRetries(requestCompanyId, () => this.tenant.companyId(), () =>
        this.api.getWorkflows(),
      );
      if (this.tenant.companyId() !== requestCompanyId) return;
      this.workflowsSignal.set(workflows.map(mapWorkflowContractToUiModel));
      this.loadedCompanyIdSignal.set(requestCompanyId);
    } catch (e) {
      if (this.tenant.companyId() !== requestCompanyId) return;
      if (isCompanyChangedAbort(e)) return;
      this.errorSignal.set(this.i18n.t('workflows.store.loadError'));
      this.workflowsSignal.set([]);
    } finally {
      if (this.tenant.companyId() === requestCompanyId) {
        this.loadingSignal.set(false);
      }
    }
  }

  getById(workflowId: string | null): WorkflowUiModel | null {
    if (!workflowId) return null;
    return this.workflows().find((workflow) => workflow.id === workflowId) ?? null;
  }

  getByProject(projectId: string | null): WorkflowUiModel[] {
    if (!projectId) return [];
    return this.workflows().filter((workflow) => workflow.projectId === projectId);
  }

  reset(): void {
    this.loadedCompanyIdSignal.set(null);
    this.workflowsSignal.set([]);
    this.loadingSignal.set(false);
    this.errorSignal.set(null);
  }
}
